import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ComboChart from "./components/ComboChart";
import AvgIncomeChart from "./components/AvgIncomeChart";
import TrendControlBar from "./components/TrendControlBar";
import {
  setTrendPeriodWithDateRange,
  setTrendDateRange,
  setCompletedOnly
} from "../slice";
import "./trend.css";

const TrendView = () => {
  const dispatch = useDispatch();
  const {
    trendData,
    trendLoading,
    trendError,
    trendPeriod,
    trendStartDate,
    trendEndDate,
    completedOnly,
    trendViewOptions
  } = useSelector((state) => state.dashboard);

  const [showIncome, setShowIncome] = useState(true);
  const [showJobs, setShowJobs] = useState(true);

  // Apply trendViewOptions when component mounts or when trendViewOptions changes
  useEffect(() => {
    if (trendViewOptions) {
      setShowIncome(trendViewOptions.showIncome);
      setShowJobs(trendViewOptions.showJobs);
    }
  }, [trendViewOptions]);

  // Brush synchronization state
  const [brushStartIndex, setBrushStartIndex] = useState(0);
  const [brushEndIndex, setBrushEndIndex] = useState(0);

  // Handle brush change - memoized to prevent unnecessary re-renders
  const handleBrushChange = useCallback((brushData) => {
    if (brushData && brushData.startIndex !== undefined && brushData.endIndex !== undefined) {
      const dataLength = trendData?.time_series?.length || 0;
      if (dataLength === 0) return;

      // Use the values from Recharts directly
      let newStart = brushData.startIndex;
      let newEnd = brushData.endIndex;

      // Right boundary check: ensure endIndex doesn't exceed data length
      if (newEnd >= dataLength) {
        newEnd = dataLength - 1;
      }

      // Left boundary check: ensure startIndex is not negative
      if (newStart < 0) {
        newStart = 0;
      }

      // Ensure start <= end
      if (newStart > newEnd) {
        newStart = newEnd;
      }

      setBrushStartIndex(newStart);
      setBrushEndIndex(newEnd);
    }
  }, [trendData]);

  // Calculate brush indices - show all data by default
  useEffect(() => {
    if (!trendData?.time_series || trendData.time_series.length === 0) {
      setBrushStartIndex(0);
      setBrushEndIndex(0);
      return;
    }

    const timeSeries = trendData.time_series;

    // Show all available data when period changes
    const startIdx = 0;
    const endIdx = timeSeries.length - 1;

    setBrushStartIndex(startIdx);
    setBrushEndIndex(endIdx);
  }, [trendData, trendStartDate, trendEndDate, trendPeriod]);

  const handlePeriodChange = (period) => {
    dispatch(setTrendPeriodWithDateRange(period));
  };

  const handleDateRangeChange = (startDate, endDate) => {
    dispatch(setTrendDateRange({ startDate, endDate }));
  };

  const handleCompletedOnlyChange = (checked) => {
    dispatch(setCompletedOnly(checked));
  };

  if (trendLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (trendError) {
    return (
      <div className="error-container alert alert-danger">
        Error loading trend data: {trendError}
      </div>
    );
  }

  return (
    <div className="trend-section">
      {/* Trend Control Bar */}
      <TrendControlBar
        selectedPeriod={trendPeriod}
        onPeriodChange={handlePeriodChange}
        startDate={trendStartDate}
        endDate={trendEndDate}
        onDateRangeChange={handleDateRangeChange}
        completedOnly={completedOnly}
        onCompletedOnlyChange={handleCompletedOnlyChange}
      />

      {/* Income vs Jobs Section */}
      <div className="trend-container">
        <div className="trend-header">
          <div className="trend-title-section">
            <div className="trend-title-left">
              <h3 className="trend-title">Income vs Jobs</h3>
              <div className="trend-checkboxes">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={showIncome}
                    onChange={(e) => setShowIncome(e.target.checked)}
                  />
                  <span className="checkbox-label">Income</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={showJobs}
                    onChange={(e) => setShowJobs(e.target.checked)}
                  />
                  <span className="checkbox-label">Jobs</span>
                </label>
              </div>
            </div>
            <div className="trend-legend">
              <div className="legend-item">
                <div className="legend-line-container">
                  <div className="legend-line" />
                  <div className="legend-dot" />
                </div>
                <span>Income</span>
              </div>
              <div className="legend-item">
                <div className="legend-square" style={{backgroundColor: '#075AD8'}}></div>
                <span>Jobs</span>
              </div>
              {!completedOnly && (
                <div className="legend-item">
                  <div className="legend-square" style={{backgroundColor: '#ADB5BD'}}></div>
                  <span>Incomplete Jobs</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="trend-chart">
          <ComboChart
            data={trendData?.time_series || []}
            showIncome={showIncome}
            showJobs={showJobs}
            completedOnly={completedOnly}
            brushStartIndex={brushStartIndex}
            brushEndIndex={brushEndIndex}
            onBrushChange={handleBrushChange}
          />
        </div>
      </div>

      {/* Avg Income per Job Section */}
      <div className="trend-container">
        <div className="trend-header">
          <div className="trend-title-section">
            <h3 className="trend-title">Avg Income per Job</h3>
            <div className="trend-legend">
              <div className="legend-item">
                <div className="legend-line-container">
                  <div className="legend-line" />
                  <div className="legend-dot" />
                </div>
                <span>Avg$/Job</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="trend-chart">
          <AvgIncomeChart
            data={trendData?.time_series || []}
            brushStartIndex={brushStartIndex}
            brushEndIndex={brushEndIndex}
            onBrushChange={handleBrushChange}
          />
        </div>
      </div>
    </div>
  );
};

export default TrendView;